import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { loginEnabled } from '../config';
import { safeNextPathOr } from './safeNext';
import type { User } from './oidc';

interface AuthValue {
  enabled: boolean;
  user: User | null;
  loading: boolean;
  error: string | null;
  login(next?: string): void;
  logout(): void;
  /** callback 頁專用。回傳登入完成後該去的站內路徑。 */
  completeLogin(): Promise<string>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(loginEnabled);
  const [error, setError] = useState<string | null>(null);

  // 開站時看看 sessionStorage 裡有沒有還活著的 session。
  useEffect(() => {
    if (!loginEnabled) return;
    let cancelled = false;
    let detachExpired: (() => void) | undefined;
    void (async () => {
      try {
        const { getUserManager } = await import('./oidc');
        const manager = getUserManager();
        const u = await manager.getUser();
        if (!cancelled) {
          const expired = () => setUser(null);
          manager.events.addAccessTokenExpired(expired);
          detachExpired = () => manager.events.removeAccessTokenExpired(expired);
        }
        if (!cancelled) setUser(u && !u.expired ? u : null);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      detachExpired?.();
    };
  }, []);

  const login = useCallback((next?: string) => {
    void (async () => {
      try {
        const { getUserManager } = await import('./oidc');
        // `next` 只允許站內路徑，避免 open redirect（C03 §5）。
        await getUserManager().signinRedirect({ state: { next: safeNextPathOr(next, '/me') } });
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, []);

  const logout = useCallback(() => {
    void (async () => {
      try {
        const { getUserManager } = await import('./oidc');
        // RP-initiated logout：oidc-client-ts 會帶 id_token_hint + post_logout_redirect_uri
        const manager = getUserManager();
        const previous = await manager.getUser();
        await manager.removeUser();
        setUser(null);
        await manager.signoutRedirect({ id_token_hint: previous?.id_token });
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, []);

  const completeLogin = useCallback(async (): Promise<string> => {
    const { getUserManager } = await import('./oidc');
    const u = await getUserManager().signinRedirectCallback();
    setUser(u);
    const state = u.state as { next?: unknown } | undefined;
    return safeNextPathOr(state?.next, '/me');
  }, []);

  const value = useMemo<AuthValue>(
    () => ({ enabled: loginEnabled, user, loading, error, login, logout, completeLogin }),
    [user, loading, error, login, logout, completeLogin],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth 必須放在 <AuthProvider> 之內');
  return ctx;
}
